import { NavLink, useLocation } from "react-router-dom";
import { useState } from "react";
import {
  Building2,
  CheckSquare,
  FileText,
  AlertTriangle,
  ClipboardList,
  BarChart3,
  Home,
  Settings,
  Zap,
  Gavel,
  Shield,
  Users,
  AlertCircle,
  Archive,
  PenTool,
  UserCog,
  Lock,
  Scale,
  Activity,
  Crown,
  Sparkles,
  User
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { usePricingAccess } from "@/hooks/usePricingAccess";
import { useRoleAccess } from "@/hooks/useRoleAccess";
import { useUserPreferences } from "@/hooks/useUserPreferences";

import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
  SidebarHeader
} from "@/components/ui/sidebar";
import { ChevronDown, ChevronRight } from "lucide-react";

const navigationGroups = [
  {
    title: "Main",
    items: [
      { title: "Dashboard", url: "/dashboard", icon: Home, requiresPermission: null, requiresFeature: null },
      { title: "Compliance Status", url: "/compliance-status", icon: Activity, requiresPermission: null, requiresFeature: null },
      { title: "Emergency Actions", url: "/emergency-actions", icon: AlertTriangle, requiresPermission: null, requiresFeature: null, highlight: true },
    ]
  },
  {
    title: "Daily Operations",
    items: [
      { title: "Checklists", url: "/checklists", icon: CheckSquare, requiresPermission: 'canCreateChecklists', requiresFeature: null },
      { title: "Templates", url: "/templates", icon: ClipboardList, requiresPermission: 'canManageTemplates', requiresFeature: null },
      { title: "Crystal Templates", url: "/crystal-templates", icon: Sparkles, requiresPermission: null, requiresFeature: 'crystalTemplatesBasic' },
      { title: "Incidents & Actions", url: "/incidents", icon: AlertCircle, requiresPermission: 'canManageIncidents', requiresFeature: null },
      { title: "Locations & Sites", url: "/units", icon: Building2, requiresPermission: 'canManageUnits', requiresFeature: null },
    ]
  },
  {
    title: "Compliance",
    items: [
      { title: "Reports", url: "/reports", icon: FileText, requiresPermission: 'canViewReports', requiresFeature: 'advancedReports' },
      { title: "Law Packs", url: "/law-packs", icon: Gavel, requiresPermission: null, requiresFeature: null },
    ]
  },
  {
    title: "Account",
    items: [
      { title: "My Profile", url: "/profile", icon: User, requiresPermission: null, requiresFeature: null },
      { title: "Quick Setup", url: "/quick-setup", icon: Zap, requiresPermission: null, requiresFeature: null, hideAfterSetup: true },
    ]
  }
];

// Advanced / admin items collapsed under a single "More" section
const moreGroup = {
  title: "More",
  items: [
    { title: "Users & Roles", url: "/user-management", icon: Users, requiresPermission: 'canManageUsers', requiresFeature: null },
    { title: "Compliance Rules", url: "/compliance-settings", icon: Shield, requiresPermission: 'canManageCompliance', requiresFeature: null },
    { title: "Site Overrides", url: "/site-overrides", icon: Lock, requiresPermission: 'canManageCompliance', requiresFeature: 'customLawPacks' },
    { title: "Signatures", url: "/signatures", icon: PenTool, requiresPermission: null, requiresFeature: null },
    { title: "Audit Trail", url: "/audit-trail", icon: Archive, requiresPermission: 'canViewAuditTrail', requiresFeature: 'auditTrail' },
    { title: "Export Center", url: "/export-center", icon: Archive, requiresPermission: 'canExportData', requiresFeature: null },
    { title: "Legal Disclaimers", url: "/legal-disclaimers", icon: Scale, requiresPermission: 'canManageSettings', requiresFeature: null },
    { title: "System Preferences", url: "/system-preferences", icon: Settings, requiresPermission: 'canManageSettings', requiresFeature: null },
  ]
};

export function AppSidebar() {
  const { state } = useSidebar();
  const location = useLocation();
  const collapsed = state === "collapsed";
  const { hasFeature, currentTier } = usePricingAccess();
  const { hasPermission } = useRoleAccess();
  const { hasCompletedSetup } = useUserPreferences();

  const isActive = (path: string) => location.pathname === path;

  const isItemAvailable = (item: any) => {
    // Hide setup items if setup is completed
    if (item.hideAfterSetup && hasCompletedSetup) {
      return false;
    }
    
    if (item.requiresPermission && !hasPermission(item.requiresPermission)) {
      return false;
    }
    if (item.requiresFeature && !hasFeature(item.requiresFeature)) {
      return false;
    }
    return true;
  };

  const availableMoreItems = moreGroup.items.filter(isItemAvailable);
  const moreContainsActive = availableMoreItems.some(i => isActive(i.url));
  const [moreOpen, setMoreOpen] = useState<boolean>(moreContainsActive);

  const renderMenuItem = (item: any) => {
    const needsUpgrade = item.requiresFeature && !hasFeature(item.requiresFeature as any);
    return (
      <SidebarMenuItem key={item.title}>
        <SidebarMenuButton asChild>
          <NavLink
            to={item.url}
            className={({ isActive }) => {
              let classes = 'flex items-center gap-3 px-3 py-2 rounded-md transition-all duration-200';
              if ((item as any).highlight) {
                classes += ' border-l-4 border-red-500 bg-red-500/5';
              }
              if (isActive) {
                classes += ' bg-primary text-primary-foreground font-medium shadow-sm';
              } else {
                classes += ' hover:bg-accent/80 hover:text-accent-foreground';
              }
              if (needsUpgrade) classes += ' opacity-70';
              return classes;
            }}
          >
            <item.icon className={`h-4 w-4 shrink-0 ${collapsed ? 'mx-auto' : ''}`} />
            {!collapsed && (
              <div className="flex-1 min-w-0 flex items-center gap-2">
                <span className="font-medium text-sm leading-tight truncate">{item.title}</span>
                {needsUpgrade && <Crown className="h-3 w-3 text-amber-500 shrink-0" />}
              </div>
            )}
          </NavLink>
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  };

  return (
    <Sidebar className="border-r border-white/10">
      <SidebarHeader className="p-6 border-b border-white/10">
        <NavLink to="/dashboard" className="flex items-center gap-4 hover:opacity-80 transition-opacity">
          <div className="w-10 h-10 bg-gradient-to-br from-primary to-secondary rounded-lg flex items-center justify-center shadow-lg">
            <BarChart3 className="w-5 h-5 text-white" />
          </div>
          {!collapsed && (
            <div className="space-y-0.5">
              <h2 className="font-semibold text-base leading-tight">Crystal HACCP</h2>
              <p className="text-sm text-muted-foreground">Food Safety System</p>
            </div>
          )}
        </NavLink>
      </SidebarHeader>

      <SidebarContent className="py-4">
        {navigationGroups.map((group, groupIndex) => {
          const availableItems = group.items.filter(isItemAvailable);
          
          if (availableItems.length === 0) return null;
          
          return (
            <SidebarGroup key={group.title} className={`${groupIndex > 0 ? 'mt-4' : 'mt-1'} mb-1`}>
              {!collapsed && (
                <SidebarGroupLabel className="px-3 py-1 text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">
                  {group.title}
                </SidebarGroupLabel>
              )}
              <SidebarGroupContent>
                <SidebarMenu className="space-y-0.5">
                  {availableItems.map(renderMenuItem)}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          );
        })}

        {/* Collapsible "More" section — hides admin/rare-use items by default */}
        {availableMoreItems.length > 0 && (
          <SidebarGroup className="mt-4 mb-1">
            <SidebarGroupContent>
              <SidebarMenu className="space-y-0.5">
                {!collapsed && (
                  <button
                    type="button"
                    onClick={() => setMoreOpen(o => !o)}
                    className="w-full flex items-center gap-2 px-3 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide hover:text-foreground transition"
                  >
                    {moreOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                    More
                  </button>
                )}
                {(collapsed || moreOpen) && availableMoreItems.map(renderMenuItem)}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}

        {/* Pricing Tier Info */}
        {!collapsed && (
          <div className="mt-auto p-4">
            <div className="glass-panel-elevated p-4 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Crown className="h-4 w-4 text-amber-500" />
                <span className="font-medium text-sm">{currentTier.name} Plan</span>
              </div>
              <p className="text-xs text-muted-foreground mb-3">
                {currentTier.maxUnits === 999 ? 'Unlimited' : `Up to ${currentTier.maxUnits}`} units
              </p>
              {currentTier.name !== 'Enterprise' && (
                <Button size="sm" variant="outline" className="w-full" asChild>
                  <NavLink to="/pricing">Upgrade</NavLink>
                </Button>
              )}
            </div>
          </div>
        )}
      </SidebarContent>

    </Sidebar>
  );
}